import { Button, ButtonGroup, Card, Col, Nav, Row, Tab } from "react-bootstrap";
import ConfirmationModal from "@enerex/unified-ui-react/ConfirmationModal";
import {
  companyTabContents,
  mockDataAliasesInfo,
  mockDataContactInfo,
  mockDataStateServices,
} from "../constants";
import { DEV_URL, rpost, rget } from "../../../utils/requests";
import { useEffect, useState } from "react";
import { LicenseAndCaseHistories } from "./LicenseAndCaseHistories";
import { ContactInfo } from "./ContactInfo";
import { AliasesData } from "./AliasesService";
import { SupplierRateClasses } from "./SupplierRateClasses";
import useShowSnackbar from "hooks/useShowSnackbar";
import { useAuth } from "providers/CustomAuthProvider";

const DEFAULT_SCREENSHOT_ZOOM = 100;
const MIN_SCREENSHOT_ZOOM = 50;
const MAX_SCREENSHOT_ZOOM = 300;
const SCREENSHOT_ZOOM_STEP = 25;

export const CompanyDetailsV2 = ({
  companyLicenseData,
  companyContactData,
  companyAddressData,
  companyId,
  company,
  subscribed,
  aliases,
  contacts,
  onRefreshAliases,
  onRefreshDomains,
}) => {
  //const [activeTab, setActiveTab] = useState(companyTabContents[0].title);
  //const [data, setData] = useState();
  const [show, setShow] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState("");
  const [screenshotZoom, setScreenshotZoom] = useState(DEFAULT_SCREENSHOT_ZOOM);
  const [isScreenshotFullscreen, setIsScreenshotFullscreen] = useState(false);
  const snackbar = useShowSnackbar();
  const { isSystemAdmin } = useAuth();

  const handleShow = (url) => {
    setScreenshotUrl(url);
    setScreenshotZoom(DEFAULT_SCREENSHOT_ZOOM);
    setIsScreenshotFullscreen(false);
    setShow(true);
  };

  const changeDisplayName = async (AliasId, aliasName) => {
    try {
      const setDisplayNameResponse = await rpost(
        `${DEV_URL}/api/company/ChangeDisplayName`,
        { CompanyId: companyId, AliasId: AliasId }
      );
      // API returns string "Display name updated!!" on success (status 200)
      // rpost returns error.response on error, which has status 400/500 etc
      if (setDisplayNameResponse && setDisplayNameResponse.status === 200) {
        snackbar(
          `Display name changed to "${aliasName}" successfully`,
          "success"
        );
        window.location.reload();
      } else {
        const errorMessage =
          setDisplayNameResponse?.data?.message ||
          setDisplayNameResponse?.data ||
          "Failed to change display name";
        snackbar(errorMessage, "error");
      }
    } catch (error) {
      console.error("Error changing display name:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to change display name";
      snackbar(errorMessage, "error");
    }
  };
  const swapParentChild = async (ChildId) => {
    await rpost(`${DEV_URL}/api/company/SwapParentChaild`, {
      CompanyId: companyId,
      AliasId: ChildId,
    });
    window.location.reload();
  };
  const removeAlias = async (AliasId) => {
    await rget(`${DEV_URL}/api/company/RemoveAlias/${AliasId}`);
    if (onRefreshAliases) {
      await onRefreshAliases();
    }
  };
  const handleClose = () => {
    setShow(false);
    setScreenshotZoom(DEFAULT_SCREENSHOT_ZOOM);
    setIsScreenshotFullscreen(false);
  };

  const zoomScreenshot = (amount) => {
    setScreenshotZoom((currentZoom) =>
      Math.min(
        MAX_SCREENSHOT_ZOOM,
        Math.max(MIN_SCREENSHOT_ZOOM, currentZoom + amount)
      )
    );
  };
  //const handleActiveTab = (tab) => {
  //    setActiveTab(tab);
  //}

  //const dataObjects = {
  //    "Licenses": companyLicenseData,
  //    "Contacts": mockDataContactInfo,
  //    "Aliases": mockDataAliasesInfo
  //  };

  // API will be called here
  // if (companyLicenseData) {
  //     companyLicenseData.screenShotEvent = handleShow;
  // }
  useEffect(() => {
    //setData(dataObjects[activeTab]);
  }, [
    companyLicenseData,
    companyContactData,
    companyAddressData,
    mockDataAliasesInfo,
  ]);
  return (
    <>
      <ConfirmationModal
        dialogClassName="screenshot-preview-modal"
        size="xl"
        fullscreen={isScreenshotFullscreen}
        scrollable
        show={show}
        onHide={handleClose}
        title="Preview Image"
        confirmButtonLabel="Close"
        confirmButtonVariant="primary"
        onConfirm={handleClose}
      >
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <ButtonGroup aria-label="Screenshot zoom controls">
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              aria-label="Zoom out"
              disabled={screenshotZoom <= MIN_SCREENSHOT_ZOOM}
              onClick={() => zoomScreenshot(-SCREENSHOT_ZOOM_STEP)}
            >
              −
            </Button>
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              aria-label="Reset zoom"
              disabled={screenshotZoom === DEFAULT_SCREENSHOT_ZOOM}
              onClick={() => setScreenshotZoom(DEFAULT_SCREENSHOT_ZOOM)}
            >
              <span aria-live="polite">{screenshotZoom}%</span>
            </Button>
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              aria-label="Zoom in"
              disabled={screenshotZoom >= MAX_SCREENSHOT_ZOOM}
              onClick={() => zoomScreenshot(SCREENSHOT_ZOOM_STEP)}
            >
              +
            </Button>
          </ButtonGroup>
          <Button
            type="button"
            variant="outline-primary"
            size="sm"
            aria-label={isScreenshotFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            onClick={() => setIsScreenshotFullscreen((isFullscreen) => !isFullscreen)}
          >
            {isScreenshotFullscreen ? "Exit fullscreen" : "Fullscreen"}
          </Button>
        </div>
        <div
          className="border rounded bg-light text-center"
          data-testid="screenshot-viewport"
          style={{
            maxHeight: isScreenshotFullscreen ? "calc(100vh - 190px)" : "65vh",
            overflow: "auto",
          }}
        >
          <img
            alt="License screenshot"
            src={screenshotUrl}
            style={{
              display: "block",
              height: "auto",
              margin: screenshotZoom <= DEFAULT_SCREENSHOT_ZOOM ? "0 auto" : "0",
              maxWidth: "none",
              width: `${screenshotZoom}%`,
            }}
          />
        </div>
      </ConfirmationModal>
      <div>
        <LicenseAndCaseHistories
          fetchedData={companyLicenseData}
          companyId={companyId}
          subscribed={subscribed}
          onScreenshotClick={handleShow}
        ></LicenseAndCaseHistories>
        <ContactInfo
          fetchedData={contacts || mockDataContactInfo}
          companyId={companyId}
          contacts={contacts}
        ></ContactInfo>
        <AliasesData
          fetchedData={aliases || mockDataAliasesInfo}
          companyId={companyId}
          aliases={aliases}
          onSetDisplayName={changeDisplayName}
          onRemoveAlias={removeAlias}
          onSwapParent={swapParentChild}
          priviladge={{ superUser: true }}
          onRefreshAliases={onRefreshAliases}
          onRefreshDomains={onRefreshDomains}
        ></AliasesData>

        {isSystemAdmin ? (
          <SupplierRateClasses companyId={companyId} company={company} />
        ) : null}


        {/*<Tab.Container activeKey={activeTab} onSelect={handleActiveTab}>*/}
        {/*    <Nav variant='tabs' className='nav-bordered' as='ul'>*/}
        {/*        {companyTabContents.map((tab, index) => {*/}
        {/*            return(*/}
        {/*            <Nav.Item key={index} as="li">*/}
        {/*                <Nav.Link href="#" eventKey={tab.title} className={activeTab === tab.title ? "tab-bg" : ""} >*/}
        {/*                    <span className={`d-none d-md-block font-sans-serif ${activeTab === tab.title ? 'fs-active-tab-color' : ''}`}>{tab.title}</span>*/}
        {/*                </Nav.Link>*/}
        {/*            </Nav.Item>*/}
        {/*        )})}*/}
        {/*    </Nav>*/}
        {/*    <Tab.Content>*/}
        {/*        {companyTabContents.map((tab, index)=> {*/}
        {/*            return(*/}
        {/*            <Tab.Pane eventKey={tab.title} id={tab.id} key={index}>*/}
        {/*                {data && <tab.content fetchedData={data} companyId={companyId}/>}*/}
        {/*            </Tab.Pane>*/}
        {/*        )})}*/}
        {/*    </Tab.Content>*/}
        {/*</Tab.Container>*/}
      </div>
    </>
  );
};
